// price-history.jsx — cross-job material price tracking.
import { ambiguousSkuSet } from './invoice-parser.js';
import { attachLocalFile } from './doc-provenance-core.js';
import React from 'react';
import {
  fmtUSD, fmtDelta, safeUrl, firestoreSafe, toCSV, downloadFile, relativeTime,
  invoiceDateOf, invoiceInRange, dateRangePresets, useFocusTrap,
  ROLES, ROLE_LABEL, ROLE_COLOR,
  usesSubInvoices, useJobsStore, useJobData, invoiceStore, deleteJobDeep, useVendorRules,
  normalizeUOM, isFootageUOM, stickFeet, itemFootage, convertToQuoteBasis, summarizeMatch,
  isAutoSku, itemKeyOf, levenshtein, fracToNum, normalizeForMatch, matchNormalize, matchLineItems,
  aggregateInvoices, compareInvoices, materialState, classifyRows, MATERIAL_STATE_META,
  getJobTrades, getSegments, lineTotal, subtotal, tax, grandTotal, pricesDiffer,
  statedInvoiceTotal, statedInvoiceTax, invoiceSpend,
  SAMPLE_VENDORS, INVOICE_A, INVOICE_B, SEED_VENDOR_RULES,
} from './invodiff-data.jsx';
import { Icon } from './invodiff-input.jsx';
import { lineTypeOf } from './invoice-parser.js';
import { DocProvenanceModal } from './doc-provenance.jsx';
//
// Aggregates every priced line item across ALL jobs/invoices in the shared
// `jobs` collection and groups them by vendor + material identity (the same
// identity the matcher uses: real SKU, else the synonym-normalized description).
// The estimator can search a material — e.g. "1-1/8 ACR" from Porter — and see
// every invoice that material appeared on and what was charged each time, with
// the price trend over time. Pure read/aggregation; no new persistence.

// Unit prices carry more precision than the 2-decimal money formatter (a
// contract unit can be $10.3205), so show 2–4 decimals as needed.
//
// The formatters are built once. `n.toLocaleString(locale, opts)` constructs a
// fresh Intl formatter on every call, and the material list formats several
// values per row across hundreds of rows on every open — that construction,
// not the aggregation, was most of this screen's script time. Same locale and
// options as the calls they replace, so the output is identical.
const UNIT_FMT_2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const UNIT_FMT_4 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const PH_DATE_FMT = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
const PH_INT_FMT  = new Intl.NumberFormat();

const fmtUnit = (n) => {
  if (n == null || !isFinite(n)) return '—';
  const dp = Math.abs(n) < 100 && Math.round(n * 100) !== n * 100 ? 4 : 2;
  return '$' + (dp === 4 ? UNIT_FMT_4 : UNIT_FMT_2).format(n);
};

const PH_SORTS = [
  { key: 'recent', label: 'Most recent' },
  { key: 'count',  label: 'Most purchased' },
  { key: 'swing',  label: 'Biggest price change' },
  { key: 'name',   label: 'Name (A–Z)' },
];

// Material list comparators — static, so they live at module scope (a stable
// reference per render keeps memoized children honest).
const PH_CMP = {
  recent: (a, b) => (b.lastDate || '').localeCompare(a.lastDate || ''),
  count:  (a, b) => b.count - a.count,
  swing:  (a, b) => Math.abs(b.trendPct) - Math.abs(a.trendPct),
  name:   (a, b) => (a.desc || '').localeCompare(b.desc || ''),
};

// Pretty invoice date ("Mar 5, 2026"); falls back to the raw string.
const phFmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00');
  return isNaN(dt) ? d : PH_DATE_FMT.format(dt);
};

// Whole days between an invoice date and today (null when undated/unparseable).
const phDaysSince = (d) => {
  if (!d) return null;
  const dt = new Date(d + 'T00:00:00');
  if (isNaN(dt)) return null;
  const a = new Date(); a.setHours(0, 0, 0, 0);
  dt.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((a - dt) / 86400000));
};
const phDaysAgo = (n) =>
  n == null ? '' : n === 0 ? 'today' : n === 1 ? 'yesterday' : `${PH_INT_FMT.format(n)} days ago`;

// "+$2.05 (+8.8%)" — a signed price change in both dollars and percent. Uses a
// real minus glyph so positive/negative read cleanly in tabular numbers.
const phFmtChange = (abs, pct) => {
  const sign = abs > 0 ? '+' : abs < 0 ? '−' : '';
  return `${sign}${fmtUnit(Math.abs(abs))} (${sign}${Math.abs(pct).toFixed(1)}%)`;
};

// Stable material identity within a vendor — mirrors the matcher's itemKeyOf:
// a real vendor SKU is the strongest signal, otherwise the synonym-normalized
// description (so "alum tape" and "aluminum tape" track as one material).
// Canonical vendor key so the same supplier written differently collapses into
// one: case-insensitive; punctuation/symbols (& / . , - etc.) dropped; connector
// words (and/the) and trailing legal/entity suffixes (Inc, LLC, Co, Corp, Ltd, …)
// stripped. So "Porter Pipe & Supply, Inc." / "PORTER PIPE AND SUPPLY" /
// "porter pipe/supply" all map to one vendor. (Display name chosen in groupMaterials.)
const VENDOR_NOISE_RE = /\b(inc|incorporated|llc|llp|lp|co|corp|corporation|company|ltd|limited|plc|and|the)\b/g;
// Branch / location qualifier ("/Chicago Branch", "Chicago Branch", "West Division").
const VENDOR_BRANCH_RE = /\b([a-z]+\s+)?(branch|warehouse|division|dept|department|location)\b/g;
const canonicalVendor = (v) => {
  let s = (v || '').toLowerCase();
  s = s.replace(/[&._,'"()/\\\-]+/g, ' '); // symbols (incl. &, /) → space
  s = s.replace(VENDOR_BRANCH_RE, ' ');    // drop branch/location designations
  s = s.replace(VENDOR_NOISE_RE, ' ');     // connectors + legal suffixes
  return s.replace(/\s+/g, ' ').trim() || 'unknown vendor';
};

const materialKey = (vendor, sku, desc) => {
  const id = (sku && !isAutoSku(sku))
    ? `sku:${sku.toLowerCase()}`
    : `desc:${matchNormalize(desc)}`;
  return `${canonicalVendor(vendor)}::${id}`;
};

// Flatten jobs → invoices → priced line items into individual purchase records.
function buildPurchaseRecords(jobs) {
  const records = [];
  let uid = 0;
  for (const job of jobs || []) {
    // Archived jobs are hidden from price history (their purchases no longer
    // reflect active pricing). Deleted jobs drop out via the live snapshot.
    if (job.active === false) continue;
    const jobVendor = job.quote?.vendor || '';
    for (const inv of (job.invoices || [])) {
      if (inv._hidden) continue;
      // Credit memos / returns reduce spend; they aren't purchases, so excluding
      // them keeps price history from logging bogus negative-quantity "buys".
      if (inv.isCredit) continue;
      const vendor   = inv.vendor || jobVendor || 'Unknown vendor';
      const invItems = inv.items || [];
      // A SKU that's non-unique within this document is a series/family label,
      // not a part number — fall back to the description for material identity
      // so distinct items sharing the label don't collapse into one material.
      const ambig = ambiguousSkuSet(invItems);
      // Index-based loop so each record keeps its position in inv.items — the
      // document preview uses it to pre-select (highlight) the right line.
      for (let itemIndex = 0; itemIndex < invItems.length; itemIndex++) {
        const it     = invItems[itemIndex];
        // Lump-sum "all sum" totals aren't a material and have no per-unit price;
        // never track them. (Informational $0 component lines fall out below via
        // the unit>0 guard.)
        if (lineTypeOf(it) === 'aggregate') continue;
        const qty    = +it.qty || 0;
        // Trust the printed unit; fall back to amount ÷ qty when only a line
        // total is present. Skip lines with no usable price (fees, $0 rows).
        const unit = it.unit > 0
          ? it.unit
          : (qty > 0 && it.amount > 0 ? +(it.amount / qty).toFixed(4) : 0);
        if (!(unit > 0)) continue;
        // Discount we received this purchase = how far the net unit sits below
        // the list/catalog price. Explains a lot of historical price variance.
        const listPrice   = it.listPrice > unit ? +it.listPrice : unit;
        const discountPct  = listPrice > unit ? +(((listPrice - unit) / listPrice) * 100).toFixed(1) : 0;
        records.push({
          _uid:      ++uid,
          vendor,
          sku:       it.sku || '',
          // Identity falls back to description when the SKU is non-unique in its
          // source document (raw sku above is preserved for display).
          skuAmbiguous: ambig.has((it.sku || '').trim()),
          desc:      it.desc || '',
          uom:       normalizeUOM(it.uom) || '',
          qty,
          unit,
          listPrice,
          discountPct,
          amount:    it.amount > 0 ? +it.amount : +(qty * unit).toFixed(2),
          date:      inv.date || '',
          invoiceId: inv.id || '',
          itemIndex,
          jobId:     job.id,
          jobNumber: job.jobNumber || '',
          jobName:   job.name || job.jobName || '',
        });
      }
    }
  }
  return records;
}

// Group purchase records into materials with price stats and a date-sorted
// purchase history.
function groupMaterials(records) {
  // One clean display name per canonical vendor: the most common raw spelling
  // (tie-break: the longer, more complete one). Keeps every grouped material's
  // vendor label identical so the filter dropdown also dedupes.
  const vendorNames = new Map(); // canonical -> Map(raw spelling -> count)
  for (const r of records) {
    const cv = canonicalVendor(r.vendor);
    let names = vendorNames.get(cv);
    if (!names) { names = new Map(); vendorNames.set(cv, names); }
    names.set(r.vendor, (names.get(r.vendor) || 0) + 1);
  }
  const vendorDisplay = new Map();
  for (const [cv, names] of vendorNames) {
    // Most common spelling; tie-break to the SHORTER (less branch/suffix noise).
    let best = null, bestN = -1;
    for (const [name, n] of names) {
      if (best === null || n > bestN || (n === bestN && name.length < best.length)) { best = name; bestN = n; }
    }
    // Standardized label: one all-caps name per vendor (dropdown + list + detail).
    vendorDisplay.set(cv, (best || '').toUpperCase());
  }

  const map = new Map();
  for (const r of records) {
    // Suppress a non-unique SKU so identity falls to the description.
    const k = materialKey(r.vendor, r.skuAmbiguous ? '' : r.sku, r.desc);
    let g = map.get(k);
    if (!g) {
      g = { key: k, vendor: vendorDisplay.get(canonicalVendor(r.vendor)) || r.vendor, sku: '', desc: '', records: [] };
      map.set(k, g);
    }
    g.records.push(r);
    // Representative SKU = first real one; representative desc = the longest
    // seen (usually the most complete vendor wording).
    if (r.sku && !isAutoSku(r.sku) && (!g.sku || isAutoSku(g.sku))) g.sku = r.sku;
    if ((r.desc || '').length > (g.desc || '').length) g.desc = r.desc;
  }

  const out = [];
  for (const g of map.values()) {
    g.records.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const units  = g.records.map((r) => r.unit);
    const first  = g.records[0];
    const latest = g.records[g.records.length - 1];
    // The purchase immediately before the latest — what estimators compare
    // against ("did we pay more than last time?").
    const prev   = g.records.length > 1 ? g.records[g.records.length - 2] : null;
    g.count    = g.records.length;
    g.min      = Math.min(...units);
    g.max      = Math.max(...units);
    g.avg      = +(units.reduce((s, u) => s + u, 0) / units.length).toFixed(4);
    g.first    = first;
    g.latest   = latest;
    g.prev     = prev;
    g.lastDate = latest.date || '';
    g.daysSince = phDaysSince(g.lastDate);
    // Headline change = latest vs. the immediately previous purchase.
    g.changeAbs = prev ? +(latest.unit - prev.unit).toFixed(4) : 0;
    g.changePct = prev && prev.unit > 0 ? +(((latest.unit - prev.unit) / prev.unit) * 100).toFixed(1) : 0;
    // Trend = first → latest price change (positive = getting more expensive).
    g.trendPct = first.unit > 0 ? +(((latest.unit - first.unit) / first.unit) * 100).toFixed(1) : 0;
    out.push(g);
  }
  return out;
}

// Responsive STEP chart of unit price over time (oldest → newest) for the
// detail view. A purchase sets the price, which then holds flat until the next
// purchase — so the line steps rather than sloping, which is how material
// pricing actually behaves. Stretches to container width; stroke stays crisp
// via non-scaling-stroke. Single price (1 point) renders a flat reference line.
function PriceChart({ records }) {
  const [hover, setHover] = React.useState(null);
  const wrapRef = React.useRef(null);
  if (!records || records.length === 0) return null;

  const W = 600, H = 150, padX = 10, padY = 20;
  const units = records.map((r) => r.unit);
  const min = Math.min(...units), max = Math.max(...units), span = max - min || 1;
  const avg = units.reduce((s, u) => s + u, 0) / units.length;
  const n = records.length;
  const step = n > 1 ? (W - padX * 2) / (n - 1) : 0;
  const xOf = (i) => padX + (n > 1 ? i * step : (W - padX * 2) / 2);
  const yOf = (u) => H - padY - ((u - min) / span) * (H - padY * 2);
  const pts = records.map((r, i) => [xOf(i), yOf(r.unit)]);
  const up   = units[n - 1] >= units[0];
  const col  = up ? 'var(--bad)' : 'var(--ok)';
  // Step path: hold the previous price horizontally, then jump vertically at the
  // new purchase. A lone purchase draws a flat line across the full width.
  const stepPts = [];
  if (n === 1) {
    stepPts.push([padX, pts[0][1]], [W - padX, pts[0][1]]);
  } else {
    pts.forEach((p, i) => {
      if (i === 0) { stepPts.push(p); return; }
      stepPts.push([p[0], pts[i - 1][1]]); // hold prior price to this x
      stepPts.push(p);                     // jump to new price
    });
  }
  const line = stepPts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const baseY = H - padY;
  const area = `${stepPts[0][0].toFixed(1)},${baseY} ${line} ${stepPts[stepPts.length - 1][0].toFixed(1)},${baseY}`;
  const avgY = yOf(avg);
  const minI = units.indexOf(min), maxI = units.indexOf(max);

  // Map the cursor to the nearest data point (x-wise) for the crosshair/tooltip.
  const onMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    let idx = 0, best = Infinity;
    for (let i = 0; i < n; i++) { const d = Math.abs(xOf(i) - x); if (d < best) { best = d; idx = i; } }
    setHover(idx);
  };

  const hr   = hover != null ? records[hover] : null;
  const hx   = hover != null ? pts[hover][0] : 0;
  const hy   = hover != null ? pts[hover][1] : 0;
  const prev = hover != null && hover > 0 ? records[hover - 1] : null;
  const delta = prev ? +(hr.unit - prev.unit).toFixed(4) : 0;
  const below = hy < H * 0.45; // flip the tooltip below the point when near the top

  return (
    <div className="ph-chart" ref={wrapRef} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg className="ph-d-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        width="100%" height={H} role="img" aria-label="Unit price over time">
        {/* average reference line */}
        <line x1={padX} y1={avgY} x2={W - padX} y2={avgY} stroke="var(--fg-dim)" strokeWidth="1"
          strokeDasharray="4 4" vectorEffect="non-scaling-stroke" opacity="0.55" />
        <polygon points={area} fill={col} opacity="0.08" />
        <polyline points={line} fill="none" stroke={col} strokeWidth="2"
          vectorEffect="non-scaling-stroke" strokeLinejoin="miter" strokeLinecap="round" />
        {/* hover crosshair */}
        {hover != null && (
          <line x1={hx} y1={padY - 8} x2={hx} y2={H - padY} stroke={col} strokeWidth="1"
            strokeDasharray="3 3" vectorEffect="non-scaling-stroke" opacity="0.7" />
        )}
        {pts.map((p, i) => (
          <circle key={records[i]._uid} cx={p[0]} cy={p[1]} r={hover === i ? 5 : 3}
            fill={hover === i ? col : 'var(--surface-1)'} stroke={col} strokeWidth="1.5"
            vectorEffect="non-scaling-stroke" />
        ))}
      </svg>

      {/* min / max / avg value markers (left gutter). High/low only when prices
          actually vary, so the tags don't pile onto the avg line on a flat chart. */}
      <span className="ph-chart-tag" style={{ top: `${avgY}px` }}>avg {fmtUnit(avg)}</span>
      {(max - min) / (avg || 1) > 0.02 && (
        <>
          {maxI >= 0 && <span className="ph-chart-tag ph-chart-hi" style={{ top: `${yOf(max)}px` }}>high {fmtUnit(max)}</span>}
          {minI >= 0 && <span className="ph-chart-tag ph-chart-lo" style={{ top: `${yOf(min)}px` }}>low {fmtUnit(min)}</span>}
        </>
      )}

      {/* hover tooltip */}
      {hr && (
        <div className={`ph-chart-tip${below ? ' is-below' : ''}`} style={{ left: `${(hx / W) * 100}%`, top: `${hy}px` }}>
          <div className="ph-tip-date">{phFmtDate(hr.date)}</div>
          <div className="ph-tip-price num">{fmtUnit(hr.unit)}{hr.uom ? `/${hr.uom}` : ''}</div>
          {hr.discountPct > 0 && <div className="ph-tip-disc">{hr.discountPct}% off list</div>}
          <div className="ph-tip-meta">{hr.jobNumber || '—'} · {hr.invoiceId || '—'}</div>
          {prev && (
            <div className={`ph-tip-delta ${delta > 0 ? 'ph-bad' : delta < 0 ? 'ph-good' : 'ph-dim'}`}>
              {delta > 0 ? '▲' : delta < 0 ? '▼' : ''} {fmtUnit(Math.abs(delta))} vs prev
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Direction indicator: ↑ price went up, ↓ down, → unchanged — with the percent
// alongside. `single` flags a material with only one purchase (no comparison).
function TrendBadge({ pct, single }) {
  if (single) return <span className="ph-trend ph-trend-flat" title="Only one purchase">→ new</span>;
  if (!pct) return <span className="ph-trend ph-trend-flat">→ 0%</span>;
  const up = pct > 0;
  return (
    <span className={`ph-trend ${up ? 'ph-trend-up' : 'ph-trend-down'}`}>
      {up ? '↑' : '↓'} {Math.abs(pct)}%
    </span>
  );
}

// Low ───── Average ───── High range bar with the latest price marked on it,
// so the estimator sees where today's price sits against everything paid before.
function PriceRangeBar({ min, max, avg, latest, uom }) {
  const span = max - min || 1;
  const flat = max - min < (avg || 1) * 0.001; // every purchase was the same price
  const pct = (v) => (flat ? 50 : Math.max(0, Math.min(100, ((v - min) / span) * 100)));
  const u = uom ? `/${uom}` : '';
  return (
    <div className="ph-range">
      <div className="ph-range-track">
        <div className="ph-range-fill" style={{ width: `${pct(latest)}%` }} />
        {!flat && <span className="ph-range-avg" style={{ left: `${pct(avg)}%` }} title={`Average ${fmtUnit(avg)}`} />}
        <span className="ph-range-dot" style={{ left: `${pct(latest)}%` }} title={`Latest ${fmtUnit(latest)}`}>
          <span className="ph-range-dot-lbl num">{fmtUnit(latest)}{u}</span>
        </span>
      </div>
      <div className="ph-range-scale">
        <span className="ph-range-end">
          <span className="ph-range-cap">Low</span>
          <span className="ph-range-val num ph-good">{fmtUnit(min)}</span>
        </span>
        <span className="ph-range-end ph-range-mid">
          <span className="ph-range-cap">Average</span>
          <span className="ph-range-val num">{fmtUnit(avg)}</span>
        </span>
        <span className="ph-range-end ph-range-hi-end">
          <span className="ph-range-cap">High</span>
          <span className="ph-range-val num ph-bad">{fmtUnit(max)}</span>
        </span>
      </div>
    </div>
  );
}

// Compact money for KPI tiles ($1.2M / $12.3k / $940).
const fmtMoneyShort = (n) => {
  const a = Math.abs(n || 0);
  if (a >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
  if (a >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'k';
  return '$' + Math.round(n || 0);
};

// Detail: every invoice this material appeared on, oldest → newest, with the
// change vs. the previous purchase so price creep is obvious.
function MaterialDetail({ material, onBack, onOpenRecord, onPreview }) {
  const rows = material.records;
  const uom  = material.latest.uom ? `/${material.latest.uom}` : '';
  const [expandedUid, setExpandedUid] = React.useState(null);
  const { prev, changeAbs, changePct, daysSince } = material;
  const tone = changeAbs > 0 ? 'ph-bad' : changeAbs < 0 ? 'ph-good' : '';

  return (
    <div className="ph-detail">
      <button className="ph-back" onClick={onBack}>
        <Icon name="chev" size={12} style={{ transform: 'rotate(180deg)' }} /> All materials
      </button>

      {/* Hero: identity, then the numbers an estimator reads first —
          latest vs. previous, the change, count, and recency. */}
      <div className="ph-d-hero">
        <div className="ph-d-id">
          <div className="ph-d-vendor">{material.vendor}</div>
          <h2 className="ph-d-desc">{material.desc || '(no description)'}</h2>
          {material.sku && !isAutoSku(material.sku) && <span className="ph-d-sku mono">{material.sku}</span>}
        </div>
        <div className="ph-d-metrics">
          <div className="ph-m ph-m-lead">
            <span className="ph-m-l">Latest price</span>
            <span className="ph-m-v num">{fmtUnit(material.latest.unit)}<span className="ph-m-u">{uom}</span></span>
          </div>
          <div className="ph-m">
            <span className="ph-m-l">Previous price</span>
            <span className="ph-m-v num">{prev ? fmtUnit(prev.unit) : '—'}</span>
          </div>
          <div className="ph-m">
            <span className="ph-m-l">Change</span>
            <span className={`ph-m-v num ${tone}`}>
              {prev ? phFmtChange(changeAbs, changePct) : <span className="ph-dim">First buy</span>}
            </span>
          </div>
          <div className="ph-m">
            <span className="ph-m-l">Purchases</span>
            <span className="ph-m-v num">{material.count}</span>
          </div>
          <div className="ph-m">
            <span className="ph-m-l">Last purchase</span>
            <span className="ph-m-v ph-m-date">{phFmtDate(material.lastDate)}</span>
            {daysSince != null && <span className="ph-m-ago">{phDaysAgo(daysSince)}</span>}
          </div>
        </div>
      </div>

      {/* Price-over-time step chart */}
      <div className="ph-d-chart">
        <PriceChart records={rows} />
        <div className="ph-d-chart-cap">
          <span><span className="ph-dim">first</span> {phFmtDate(material.first.date)} · {fmtUnit(material.first.unit)}</span>
          <span><span className="ph-dim">latest</span> {phFmtDate(material.latest.date)} · {fmtUnit(material.latest.unit)}</span>
        </div>
      </div>

      {/* Where the latest price sits across the full purchase range */}
      <div className="ph-d-range">
        <PriceRangeBar min={material.min} max={material.max} avg={material.avg}
          latest={material.latest.unit} uom={material.latest.uom} />
      </div>

      {/* Purchase timeline */}
      <div className="ph-d-tablehd">
        <span className="ph-d-tablehd-t">Purchase history</span>
        <span className="ph-d-tablehd-hint">Click a row to see its line detail</span>
      </div>
      <div className="ph-d-tablewrap">
        <div className="ph-table ph-dtable" role="table">
          <div className="ph-dtr ph-dtr-head" role="row">
            <span role="columnheader">Date</span>
            <span role="columnheader">Job</span>
            <span role="columnheader">Invoice</span>
            <span role="columnheader" className="ph-num">Qty</span>
            <span role="columnheader" className="ph-num">Unit price</span>
            <span role="columnheader" className="ph-num">Discount</span>
            <span role="columnheader" className="ph-num">Δ vs prev</span>
            <span role="columnheader" aria-hidden="true"></span>
          </div>
          {rows.map((r, i) => {
            const prev = i > 0 ? rows[i - 1] : null;
            const delta = prev ? +(r.unit - prev.unit).toFixed(4) : 0;
            const deltaPct = prev && prev.unit > 0 ? (delta / prev.unit) * 100 : 0;
            const tone = !prev ? '' : delta > 0.0001 ? 'ph-bad' : delta < -0.0001 ? 'ph-good' : '';
            const open = expandedUid === r._uid;
            return (
              <div key={r._uid} className="ph-drow">
                <button className={`ph-dtr ph-dtr-btn${open ? ' is-open' : ''}`} role="row"
                  aria-expanded={open}
                  onClick={() => setExpandedUid(open ? null : r._uid)}>
                  <span role="cell" className="ph-nowrap">{phFmtDate(r.date)}</span>
                  <span role="cell" className="ph-job" title={r.jobName || r.jobNumber}>{r.jobNumber || r.jobName || '—'}</span>
                  <span role="cell" className="mono ph-dim ph-job">{r.invoiceId || '—'}</span>
                  <span role="cell" className="ph-num ph-nowrap">{r.qty.toLocaleString()}{r.uom ? ' ' + r.uom : ''}</span>
                  <span role="cell" className="ph-num ph-unit ph-nowrap">{fmtUnit(r.unit)}</span>
                  <span role="cell" className="ph-num ph-nowrap">
                    {r.discountPct > 0
                      ? <span className="ph-disc" title={`List ${fmtUnit(r.listPrice)} → net ${fmtUnit(r.unit)}`}>{r.discountPct}% off</span>
                      : <span className="ph-dim">—</span>}
                  </span>
                  <span role="cell" className={`ph-num ph-nowrap ${tone}`}>
                    {!prev ? <span className="ph-dim">First buy</span>
                      : delta === 0 ? <span className="ph-dim">no change</span>
                      : `${delta > 0 ? '+' : '−'}${fmtUnit(Math.abs(delta))} (${deltaPct > 0 ? '+' : '−'}${Math.abs(deltaPct).toFixed(1)}%)`}
                  </span>
                  <span role="cell" className="ph-dtr-go" aria-hidden="true"><Icon name="chev" size={13} /></span>
                </button>
                {open && (
                  <div className="ph-drow-detail">
                    <div className="ph-dd-grid">
                      <div className="ph-dd-field ph-dd-wide">
                        <span className="ph-dd-l">Line description</span>
                        <span className="ph-dd-v">{r.desc || '(no description)'}</span>
                      </div>
                      {r.sku && !isAutoSku(r.sku) && (
                        <div className="ph-dd-field">
                          <span className="ph-dd-l">SKU</span>
                          <span className="ph-dd-v mono">{r.sku}</span>
                        </div>
                      )}
                      <div className="ph-dd-field">
                        <span className="ph-dd-l">As billed</span>
                        <span className="ph-dd-v num">{r.qty.toLocaleString()}{r.uom ? ' ' + r.uom : ''} × {fmtUnit(r.unit)} = {fmtUSD(r.amount)}</span>
                      </div>
                      <div className="ph-dd-field">
                        <span className="ph-dd-l">List price</span>
                        <span className="ph-dd-v num">{fmtUnit(r.listPrice)}{r.uom ? `/${r.uom}` : ''}</span>
                      </div>
                      <div className="ph-dd-field">
                        <span className="ph-dd-l">Discount</span>
                        <span className="ph-dd-v num">
                          {r.discountPct > 0
                            ? <span className="ph-good">{r.discountPct}% off list</span>
                            : <span className="ph-dim">none</span>}
                        </span>
                      </div>
                      <div className="ph-dd-field">
                        <span className="ph-dd-l">Vendor</span>
                        <span className="ph-dd-v">{r.vendor}</span>
                      </div>
                      <div className="ph-dd-field">
                        <span className="ph-dd-l">Job</span>
                        <span className="ph-dd-v">{r.jobNumber || '—'}{r.jobName ? ` · ${r.jobName}` : ''}</span>
                      </div>
                      <div className="ph-dd-field">
                        <span className="ph-dd-l">Invoice</span>
                        <span className="ph-dd-v mono">{r.invoiceId || '—'}</span>
                      </div>
                      <div className="ph-dd-field">
                        <span className="ph-dd-l">Date</span>
                        <span className="ph-dd-v">{phFmtDate(r.date)}</span>
                      </div>
                    </div>
                    <div className="ph-dd-actions">
                      {onPreview && (
                        <button className="ph-dd-open" onClick={() => onPreview(r)}>
                          <Icon name="search" size={12} /> Preview line in document
                        </button>
                      )}
                      {onOpenRecord && (
                        <button className="ph-dd-open ph-dd-open-2" onClick={() => onOpenRecord(r)}>
                          Open in {r.jobNumber || 'job'} <Icon name="external" size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Stand-in while the jobs store loads — one identity, so the memos keyed on
// `jobs` don't recompute every render.
const NO_JOBS = [];

function PriceHistoryView({ user, onOpenJob }) {
  // Jobs come from the shared session-wide store (invodiff-data.jsx) — the
  // same single subscription the dashboard/jobs list uses, with split-shape
  // jobs' invoices already joined in.
  const { jobs: storeJobs, loading } = useJobsStore();
  const jobs = storeJobs || NO_JOBS;
  const [query, setQuery]     = React.useState('');
  const [vendor, setVendor]   = React.useState('all');
  const [sortBy, setSortBy]   = React.useState('recent');
  const [selected, setSelected] = React.useState(null); // material key
  const [previewRec, setPreviewRec] = React.useState(null); // purchase record being previewed in the PDF

  const materials = React.useMemo(() => groupMaterials(buildPurchaseRecords(jobs)), [jobs]);

  const vendors = React.useMemo(
    () => Array.from(new Set(materials.map((m) => m.vendor))).sort((a, b) => a.localeCompare(b)),
    [materials]
  );

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = materials.flatMap((m) => {
      if (vendor !== 'all' && m.vendor !== vendor) return [];
      if (q && !(`${m.desc} ${m.sku} ${m.vendor}`.toLowerCase().includes(q))) return [];
      return [m];
    });
    return filtered.sort(PH_CMP[sortBy] || PH_CMP.recent);
  }, [materials, query, vendor, sortBy]);

  const selectedMaterial = React.useMemo(
    () => materials.find((m) => m.key === selected) || null,
    [materials, selected]
  );

  // Headline numbers + the actionable anomalies (materials whose price is
  // climbing) — surfaced first so the estimator sees where money is leaking
  // instead of scanning a flat list.
  const stats = React.useMemo(() => {
    let spend = 0;
    for (const m of materials) {
      for (const r of m.records) spend += r.amount;
    }
    const increases = materials.filter((m) => m.count >= 2 && m.trendPct > 0);
    const topMovers = increases.sort((a, b) => b.trendPct - a.trendPct).slice(0, 4);
    return { spend, increaseCount: increases.length, topMovers };
  }, [materials]);

  if (selectedMaterial) {
    const openRecord = (r) => {
      const job = jobs.find((j) => j.id === r.jobId);
      if (job && onOpenJob) onOpenJob(job, r.invoiceId);
    };
    // Resolve the previewed record to its actual invoice (with its uploaded PDF
    // re-attached) so the document viewer can render + highlight the line.
    let previewInv = null;
    if (previewRec) {
      const job = jobs.find((j) => j.id === previewRec.jobId);
      const inv = job && (job.invoices || []).find((iv) => iv.id === previewRec.invoiceId);
      if (inv) previewInv = attachLocalFile(inv);
    }
    return (
      <div className="ph">
        <MaterialDetail material={selectedMaterial} onBack={() => setSelected(null)}
          onPreview={(r) => setPreviewRec(r)}
          onOpenRecord={onOpenJob ? openRecord : null} />
        {previewRec && previewInv && (
          <DocProvenanceModal invoice={previewInv}
            initialSel={typeof previewRec.itemIndex === 'number' ? previewRec.itemIndex : 0}
            onClose={() => setPreviewRec(null)} />
        )}
        <PriceHistoryStyles />
      </div>
    );
  }

  return (
    <div className="ph">
      <div className="ph-hd">
        <div>
          <h1 className="ph-title">Price History</h1>
          <p className="ph-sub">Every material you've been invoiced, and what it cost over time.</p>
        </div>
      </div>

      {!loading && materials.length > 0 && (
        <div className="ph-kpis">
          <div className="ph-kpi">
            <span className="ph-kpi-l">Materials tracked</span>
            <span className="ph-kpi-v num">{materials.length}</span>
          </div>
          <div className="ph-kpi">
            <span className="ph-kpi-l">Spend tracked</span>
            <span className="ph-kpi-v num">{fmtMoneyShort(stats.spend)}</span>
          </div>
          <div className="ph-kpi">
            <span className="ph-kpi-l">Vendors</span>
            <span className="ph-kpi-v num">{vendors.length}</span>
          </div>
          <button
            className={`ph-kpi ph-kpi-btn ${sortBy === 'swing' ? 'is-on' : ''}`}
            onClick={() => setSortBy('swing')}
            title="Sort by biggest price change"
          >
            <span className="ph-kpi-l">Getting pricier</span>
            <span className={`ph-kpi-v num ${stats.increaseCount > 0 ? 'ph-bad' : ''}`}>{stats.increaseCount}</span>
          </button>
        </div>
      )}

      {!loading && !query.trim() && stats.topMovers.length > 0 && (
        <div className="ph-movers">
          <div className="ph-movers-h">
            <Icon name="flag" size={13} />
            <span>Biggest price increases</span>
            <span className="ph-movers-sub">first invoice → latest</span>
          </div>
          <div className="ph-movers-grid">
            {stats.topMovers.map((m) => (
              <button key={m.key} className="ph-mover" onClick={() => setSelected(m.key)}>
                <div className="ph-mover-top">
                  <span className="ph-mover-desc" title={m.desc}>{m.desc || '(no description)'}</span>
                  <TrendBadge pct={m.trendPct} />
                </div>
                <div className="ph-mover-mid">
                  <span className="ph-mover-vendor" title={m.vendor}>{m.vendor}</span>
                  <span className="ph-mover-meta">{m.count} buys{m.daysSince != null ? ` · ${phDaysAgo(m.daysSince)}` : ''}</span>
                </div>
                <div className="ph-mover-bot num">
                  <span className="ph-dim">{fmtUnit(m.first.unit)}</span>
                  <Icon name="arrow" size={11} />
                  <span className="ph-unit">{fmtUnit(m.latest.unit)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ph-controls">
        <div className="ph-search">
          <Icon name="search" size={14} />
          <input
            className="ph-search-input"
            placeholder="Search a material, SKU, or vendor… (e.g. 1-1/8 ACR)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search materials"
          />
          {query && <button className="ph-clear" onClick={() => setQuery('')} aria-label="Clear search"><Icon name="x" size={12} /></button>}
        </div>
        <select className="ph-select" value={vendor} onChange={(e) => setVendor(e.target.value)} aria-label="Filter by vendor">
          <option value="all">All vendors</option>
          {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="ph-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort materials">
          {PH_SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="ph-empty">Loading invoices…</div>
      ) : materials.length === 0 ? (
        <div className="ph-empty">
          <Icon name="history" size={20} />
          <div className="ph-empty-h">No invoiced materials yet</div>
          <div className="ph-empty-s">Add invoices to your jobs and their line items will be tracked here automatically.</div>
        </div>
      ) : visible.length === 0 ? (
        <div className="ph-empty">No materials match your search.</div>
      ) : (
        <div className="ph-list" role="table">
          <div className="ph-lrow ph-lhead" role="row">
            <span role="columnheader">Material</span>
            <span role="columnheader" className="ph-num">Last price</span>
            <span role="columnheader" className="ph-num">Previous</span>
            <span role="columnheader" className="ph-num">Δ</span>
            <span role="columnheader" className="ph-num">Buys</span>
            <span role="columnheader">Last purchased</span>
          </div>
          {visible.map((m) => (
            <button key={m.key} className="ph-lrow ph-lrow-btn" role="row" onClick={() => setSelected(m.key)}>
              <span role="cell" className="ph-mat">
                <span className="ph-mat-desc" title={m.desc}>{m.desc || '(no description)'}</span>
                <span className="ph-mat-sub">
                  <span className="ph-mat-vendor">{m.vendor}</span>
                  {m.sku && !isAutoSku(m.sku) && <span className="ph-mat-sku mono">{m.sku}</span>}
                </span>
              </span>
              <span role="cell" className="ph-num ph-unit" title={m.min === m.max ? '' : `range ${fmtUnit(m.min)}–${fmtUnit(m.max)}`}>{fmtUnit(m.latest.unit)}</span>
              <span role="cell" className="ph-num ph-dim">{m.prev ? fmtUnit(m.prev.unit) : '—'}</span>
              <span role="cell" className="ph-num"><TrendBadge pct={m.changePct} single={!m.prev} /></span>
              <span role="cell" className="ph-num">{m.count}</span>
              <span role="cell" className="ph-last">
                <span className="ph-last-d">{phFmtDate(m.lastDate)}</span>
                {m.daysSince != null && <span className="ph-last-ago">{phDaysAgo(m.daysSince)}</span>}
              </span>
            </button>
          ))}
        </div>
      )}

      <PriceHistoryStyles />
    </div>
  );
}

const PriceHistoryStyles = () => (
  <style>{`
    .ph { width: 100%; max-width: var(--app-max); margin: 0 auto; padding: 28px var(--app-pad) 60px; }

    /* ── View-transition motion ──────────────────────────────────────
       Opening a material (list → detail) and returning (detail → list)
       remounts the view, so a quiet rise+fade plays each time. The detail
       cascades its sections top-to-bottom; the list fades in as a unit.
       transform/opacity only, fast ease-out, fully disabled under
       reduced-motion. (Matches the dashboard's motion language.) */
    @keyframes ph-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
    @media (prefers-reduced-motion: no-preference) {
      .ph-list { animation: ph-rise .4s cubic-bezier(.23,1,.32,1) backwards; }
      .ph-detail > .ph-back,
      .ph-detail > .ph-d-hero,
      .ph-detail > .ph-d-chart,
      .ph-detail > .ph-d-range,
      .ph-detail > .ph-d-tablehd,
      .ph-detail > .ph-d-tablewrap { animation: ph-rise .44s cubic-bezier(.23,1,.32,1) backwards; }
      .ph-detail > .ph-back        { animation-delay: 0s;   }
      .ph-detail > .ph-d-hero      { animation-delay: .04s; }
      .ph-detail > .ph-d-chart     { animation-delay: .10s; }
      .ph-detail > .ph-d-range     { animation-delay: .16s; }
      .ph-detail > .ph-d-tablehd   { animation-delay: .22s; }
      .ph-detail > .ph-d-tablewrap { animation-delay: .26s; }
    }
    /* Embedded as a dashboard widget — fill the dashboard column (it already pads). */
    .jl-dash-history .ph { max-width: none; padding-left: 0; padding-right: 0; padding-top: 0; }
    .ph-hd { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
    .ph-title { font-size: 22px; font-weight: 650; margin: 0; }
    .ph-sub { margin: 4px 0 0; color: var(--fg-muted); font-size: 13px; }
    .ph-count { color: var(--fg-dim); font-size: 12px; white-space: nowrap; }

    /* KPI strip */
    .ph-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; margin-bottom: 16px; }
    .ph-kpi { background: var(--surface-2); border: 1px solid var(--border); border-radius: 10px; padding: 12px 14px;
      display: flex; flex-direction: column; gap: 4px; text-align: left; }
    .ph-kpi-l { font-size: 11px; text-transform: uppercase; letter-spacing: .03em; color: var(--fg-dim); }
    .ph-kpi-v { font-size: 21px; font-weight: 680; line-height: 1; }
    .ph-kpi-btn { cursor: pointer; transition: border-color .15s, background .15s; }
    .ph-kpi-btn:hover { border-color: var(--accent); }
    .ph-kpi-btn.is-on { border-color: var(--accent); background: oklch(from var(--accent) l c h / 0.10); }

    /* Top movers (anomaly triage) */
    .ph-movers { margin-bottom: 18px; }
    .ph-movers-h { display: flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 600; color: var(--fg-muted); margin-bottom: 8px; }
    .ph-movers-h > :first-child { color: var(--bad); }
    .ph-movers-sub { font-weight: 400; color: var(--fg-dim); font-size: 11px; }
    .ph-movers-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; }
    .ph-mover { text-align: left; background: var(--surface-1); border: 1px solid var(--border); border-left: 3px solid var(--bad);
      border-radius: 9px; padding: 11px 13px; cursor: pointer; display: flex; flex-direction: column; gap: 7px; transition: background .15s; }
    .ph-mover:hover { background: var(--surface-2); }
    .ph-mover-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .ph-mover-desc { font-size: 13px; font-weight: 550; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ph-mover-mid { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .ph-mover-vendor { font-size: 11.5px; color: var(--fg-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ph-mover-meta { font-size: 11px; color: var(--fg-dim); white-space: nowrap; flex-shrink: 0; }
    .ph-mover-bot { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--fg-dim); }

    .ph-controls { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
    .ph-search { flex: 1 1 280px; display: flex; align-items: center; gap: 8px; padding: 0 10px;
      background: var(--surface-2); border: 1px solid var(--border); border-radius: 8px; color: var(--fg-dim); }
    .ph-search-input { flex: 1; background: none; border: none; outline: none; color: var(--fg); font-size: 13.5px; padding: 9px 0; }
    .ph-clear { background: none; border: none; color: var(--fg-dim); cursor: pointer; display: flex; padding: 2px; }
    .ph-select { background: var(--surface-2); border: 1px solid var(--border); border-radius: 8px; color: var(--fg);
      font-size: 13px; padding: 0 10px; height: 38px; cursor: pointer; }

    .ph-list, .ph-table { border: 1px solid var(--border); border-radius: 10px; overflow: hidden; background: var(--surface-1); }
    .ph-lrow { display: grid; grid-template-columns: minmax(0,3fr) 96px 96px 88px 52px minmax(120px,1.4fr);
      gap: 14px; align-items: center; padding: 11px 14px; text-align: left; }
    .ph-lhead { font-size: 10.5px; text-transform: uppercase; letter-spacing: .04em; color: var(--fg-dim);
      background: var(--surface-2); border-bottom: 1px solid var(--border); }
    .ph-lrow-btn { width: 100%; background: none; border: none; border-bottom: 1px solid var(--border); cursor: pointer; font: inherit; color: inherit; }
    /* The list is every material ever invoiced — hundreds of rows, all in the
       DOM so browser find-in-page and the scrollbar stay honest. This lets the
       browser skip layout and paint for rows that are off-screen; it was the
       bulk of the time to open this screen and the Dashboard that embeds it.
       38px is a row's CONTENT height (61px less its padding and border), which
       is what contain-intrinsic-size reserves — so a skipped row takes exactly
       the space the real one will, and the scrollbar does not jump. */
    .ph-lrow-btn { content-visibility: auto; contain-intrinsic-size: auto 38px; }
    .ph-lrow-btn:last-child { border-bottom: none; }
    .ph-lrow-btn:hover { background: var(--surface-2); }
    .ph-mat { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .ph-mat-desc { font-size: 13.5px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ph-mat-sub { display: flex; align-items: center; gap: 8px; min-width: 0; }
    .ph-mat-vendor { font-size: 11.5px; color: var(--fg-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ph-mat-sku { font-size: 10.5px; color: var(--fg-dim); flex-shrink: 0; }
    .ph-last { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
    .ph-last-d { font-size: 12.5px; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .ph-last-ago { font-size: 10.5px; color: var(--fg-dim); white-space: nowrap; }
    .ph-num { text-align: right; font-variant-numeric: tabular-nums; }
    .ph-unit { font-weight: 600; }
    .ph-dim { color: var(--fg-dim); }
    .ph-good { color: var(--ok); }
    .ph-bad  { color: var(--bad); }

    .ph-trend { font-size: 11.5px; font-weight: 600; white-space: nowrap; }
    .ph-trend-up   { color: var(--bad); }
    .ph-trend-down { color: var(--ok); }
    .ph-trend-flat { color: var(--fg-dim); font-weight: 500; }

    .ph-empty { text-align: center; color: var(--fg-muted); padding: 60px 20px; display: flex; flex-direction: column; align-items: center; gap: 8px; }
    .ph-empty-h { font-size: 15px; font-weight: 600; color: var(--fg); }
    .ph-empty-s { font-size: 13px; max-width: 380px; }

    .ph-back { display: inline-flex; align-items: center; gap: 6px; background: var(--surface-2); border: 1px solid var(--border);
      border-radius: 8px; color: var(--fg-muted); font-size: 12.5px; font-weight: 500; cursor: pointer;
      padding: 7px 12px 7px 10px; margin-bottom: 16px; transition: background .15s, color .15s, border-color .15s; }
    .ph-back:hover { background: var(--surface-3); color: var(--fg); border-color: var(--fg-dim); }
    .ph-back:active { transform: translateY(0.5px); }
    /* detail — hero */
    .ph-d-hero { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; flex-wrap: wrap;
      background: var(--surface-2); border: 1px solid var(--border); border-radius: 12px; padding: 16px 18px; margin-bottom: 14px; }
    .ph-d-id { min-width: 0; flex: 1 1 200px; }
    .ph-d-vendor { font-size: 11.5px; font-weight: 600; letter-spacing: .03em; color: var(--fg-muted); margin-bottom: 3px; }
    .ph-d-desc { font-size: 20px; font-weight: 650; margin: 0 0 6px; line-height: 1.2; }
    .ph-d-sku { font-size: 11.5px; color: var(--fg-dim); background: var(--surface-3); border: 1px solid var(--border); padding: 2px 7px; border-radius: 5px; }
    /* headline metric row: latest / previous / change / count / recency */
    .ph-d-metrics { display: flex; flex-wrap: wrap; gap: 10px 22px; align-items: flex-start; }
    .ph-m { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
    .ph-m-l { font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: var(--fg-dim); font-weight: 600; }
    .ph-m-v { font-size: 16px; font-weight: 650; line-height: 1.1; white-space: nowrap; }
    .ph-m-lead .ph-m-v { font-size: 24px; font-weight: 720; letter-spacing: -.01em; }
    .ph-m-u { font-size: 11px; font-weight: 500; color: var(--fg-dim); margin-left: 2px; }
    .ph-m-date { font-size: 14px; font-weight: 550; }
    .ph-m-ago { font-size: 10.5px; color: var(--fg-dim); }

    /* detail — chart */
    .ph-d-chart { background: var(--surface-1); border: 1px solid var(--border); border-radius: 12px; padding: 12px 14px 10px; margin-bottom: 14px; }
    .ph-chart { position: relative; width: 100%; }
    .ph-d-svg { display: block; width: 100%; height: 150px; cursor: crosshair; }
    /* left-gutter value markers aligned to the avg / high / low gridlines */
    .ph-chart-tag { position: absolute; left: 8px; transform: translateY(-50%); pointer-events: none;
      font-size: 9px; font-weight: 600; letter-spacing: .02em; color: var(--fg-dim);
      background: oklch(from var(--surface-1) l c h / .82); padding: 0 4px; border-radius: 3px; white-space: nowrap; }
    .ph-chart-hi { color: var(--bad); } .ph-chart-lo { color: var(--ok); }
    /* hover tooltip */
    .ph-chart-tip { position: absolute; z-index: 6; pointer-events: none; min-width: 132px;
      background: var(--surface); border: 1px solid var(--border-strong); border-radius: 9px;
      box-shadow: var(--shadow-2); padding: 8px 11px; transform: translate(-50%, calc(-100% - 12px)); }
    .ph-chart-tip.is-below { transform: translate(-50%, 14px); }
    .ph-tip-date  { font-size: 10px; color: var(--fg-dim); }
    .ph-tip-price { font-size: 15px; font-weight: 700; letter-spacing: -.01em; }
    .ph-tip-disc  { font-size: 10.5px; font-weight: 600; color: var(--ok); margin-top: 1px; }
    .ph-tip-meta  { font-size: 10px; color: var(--fg-muted); margin-top: 3px; }
    .ph-tip-delta { font-size: 10px; font-weight: 600; margin-top: 3px; }
    .ph-d-chart-cap { display: flex; justify-content: space-between; gap: 12px; margin-top: 6px;
      font-size: 11.5px; color: var(--fg-muted); font-variant-numeric: tabular-nums; }

    /* price range bar: Low ── Average ── High with the latest price marked */
    .ph-d-range { background: var(--surface-1); border: 1px solid var(--border); border-radius: 12px;
      padding: 26px 18px 14px; margin-bottom: 16px; }
    .ph-range-track { position: relative; height: 6px; border-radius: 999px;
      background: linear-gradient(90deg, oklch(from var(--ok) l calc(c*.7) h / .4), var(--surface-3) 50%, oklch(from var(--bad) l calc(c*.7) h / .4)); }
    .ph-range-fill { position: absolute; left: 0; top: 0; height: 100%; border-radius: 999px;
      background: oklch(from var(--accent) l c h / .35); }
    .ph-range-avg { position: absolute; top: -4px; width: 2px; height: 14px; background: var(--fg-dim); transform: translateX(-50%); border-radius: 1px; }
    .ph-range-dot { position: absolute; top: 50%; width: 13px; height: 13px; border-radius: 50%;
      background: var(--accent); border: 2px solid var(--surface-1); box-shadow: var(--shadow-1);
      transform: translate(-50%, -50%); z-index: 2; }
    .ph-range-dot-lbl { position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%);
      font-size: 11.5px; font-weight: 700; color: var(--accent); white-space: nowrap; }
    .ph-range-scale { display: flex; justify-content: space-between; margin-top: 10px; }
    .ph-range-end { display: flex; flex-direction: column; gap: 2px; }
    .ph-range-mid { align-items: center; text-align: center; }
    .ph-range-hi-end { align-items: flex-end; text-align: right; }
    .ph-range-cap { font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: var(--fg-dim); font-weight: 600; }
    .ph-range-val { font-size: 14px; font-weight: 650; }

    /* detail — timeline table (scroll-safe on narrow screens) */
    .ph-d-tablehd { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 6px; }
    .ph-d-tablehd-t { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: var(--fg-dim); font-weight: 600; }
    .ph-d-tablehd-hint { font-size: 11.5px; color: var(--accent); }
    .ph-d-tablewrap { overflow-x: auto; border: 1px solid var(--border); border-radius: 10px; background: var(--surface-1); }
    .ph-dtable { border: none; border-radius: 0; min-width: 748px; }
    .ph-dtr { display: grid; grid-template-columns: 116px minmax(90px,1fr) minmax(90px,1fr) 96px 104px 84px 150px 26px;
      gap: 14px; align-items: center; padding: 10px 16px; border-bottom: 1px solid var(--border); font-size: 13px; }
    .ph-dtr:last-child { border-bottom: none; }
    .ph-dtr:not(.ph-dtr-head):hover { background: var(--surface-2); }
    .ph-dtr-head { font-size: 10px; text-transform: uppercase; letter-spacing: .05em; color: var(--fg-dim);
      background: var(--surface-2); position: sticky; top: 0; }
    /* clickable rows → expand inline to show the source line detail */
    .ph-drow:last-child .ph-dtr-btn { border-bottom: none; }
    .ph-dtr-btn { width: 100%; text-align: left; background: none; border: none; border-bottom: 1px solid var(--border);
      font: inherit; color: inherit; cursor: pointer; }
    .ph-dtr-btn.is-open { background: var(--surface-2); }
    .ph-dtr-go { display: flex; justify-content: flex-end; color: var(--fg-dim); transition: transform .15s, color .12s; }
    .ph-dtr-btn:hover .ph-dtr-go { color: var(--accent); }
    .ph-dtr-btn.is-open .ph-dtr-go { transform: rotate(90deg); color: var(--accent); }
    .ph-job { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ph-nowrap { white-space: nowrap; }
    .ph-disc { font-size: 11.5px; font-weight: 600; color: var(--ok);
      background: var(--ok-bg); border: 1px solid oklch(from var(--ok) l calc(c*.5) h / .35);
      padding: 1px 7px; border-radius: 999px; }

    /* expanded source-line detail */
    .ph-drow-detail { padding: 14px 16px 16px; background: var(--surface-2);
      border-bottom: 1px solid var(--border); }
    .ph-dd-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px 20px; }
    .ph-dd-wide { grid-column: 1 / -1; }
    .ph-dd-field { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
    .ph-dd-l { font-size: 10px; text-transform: uppercase; letter-spacing: .05em; color: var(--fg-dim); font-weight: 600; }
    .ph-dd-v { font-size: 13px; color: var(--fg); word-break: break-word; }
    .ph-dd-actions { margin-top: 14px; display: flex; flex-wrap: wrap; gap: 8px; }
    .ph-dd-open { appearance: none; cursor: pointer; font: inherit; font-size: 12.5px; font-weight: 500;
      display: inline-flex; align-items: center; gap: 7px; padding: 8px 14px; border-radius: var(--r);
      border: 1px solid oklch(from var(--accent) l calc(c*.5) h / .4); background: oklch(from var(--accent) l c h / .1); color: var(--accent);
      transition: background .12s, border-color .12s; }
    .ph-dd-open:hover { background: oklch(from var(--accent) l c h / .18); border-color: var(--accent); }
    .ph-dd-open-2 { border-color: var(--border); background: var(--surface); color: var(--fg-muted); }
    .ph-dd-open-2:hover { background: var(--surface-3); border-color: var(--border-strong); color: var(--fg); }
  `}</style>
);

export { PriceHistoryView, fmtUnit, phFmtDate, phDaysAgo };
